import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import toast from 'react-hot-toast';
import { ordersService } from '../../services/orders';
import { useAddress } from '../../hooks/useAddress';
import { useAuth } from '../../hooks/useAuth';
import { usePincodeLookup } from '../../hooks/usePincodeLookup';
import { WarehouseRow } from '../../components/WarehouseRow';
import { ChevronDown, Search, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { channelService } from '../../services/channel';

interface EditChannelOrderModalProps {
  order: any;
  onClose: () => void;
  onSuccess: (updatedOrder: any) => void;
}

export const EditChannelOrderModal: React.FC<EditChannelOrderModalProps> = ({
  order,
  onClose,
  onSuccess,
}) => {
  const isDummy = (val?: string | null) => {
    if (!val || typeof val !== 'string') return true;
    const lower = val.trim().toLowerCase();
    return (
      lower.includes('not available') ||
      lower.includes('not applicable') ||
      lower === 'n/a' ||
      lower === 'na' ||
      lower === 'null' ||
      lower === 'none'
    );
  };

  const cleanPhone = (val?: string | null) => {
    if (!val) return '';
    const digits = val.replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) {
      return digits.substring(2);
    }
    if (digits.length === 11 && digits.startsWith('0')) {
      return digits.substring(1);
    }
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const rawAddress1 = order.destination_address?.address1 || '';
  const rawAddress2 = isDummy(order.destination_address?.address2) ? '' : order.destination_address?.address2 || '';
  const initialAddress1 = rawAddress1.trim() !== '' ? rawAddress1 : rawAddress2;
  const initialAddress2 = rawAddress1.trim() !== '' ? rawAddress2 : '';

  const [formData, setFormData] = useState({
    address1: initialAddress1,
    address2: initialAddress2,
    landmark: isDummy(order.destination_address?.landmark) ? '' : order.destination_address?.landmark || '',
    city: order.destination_address?.city || '',
    state: order.destination_address?.state || '',
    pincode: order.destination_address?.pincode || '',
    contact_name: order.destination_address?.contact_name || '',
    contact_no: cleanPhone(order.destination_address?.contact_no),
    weight: order.dead_weight && Number(order.dead_weight) > 0 ? order.dead_weight : '0.5',
    // Dimensions commented out for Shopify orders
    /*
    length: order.length || '',
    breadth: order.breadth || '',
    height: order.height || '',
    */
    pickup_id: order.origin_address?.pickup_id || '',
  });

  const { user } = useAuth();
  const { data: addressData } = useAddress(user?.id as string);
  const activePickupAddresses = addressData?.pickupAddresses?.filter((item: any) => item.status === 1) || [];

  const storeId = order.store_id;
  const { data: channelAddressData } = useQuery({
    queryKey: ['channelPickupAddresses', user?.id, storeId],
    queryFn: () => channelService.fetchPickupAddresses(user?.id as string, storeId),
    enabled: !!user?.id && !!storeId && Number(storeId) > 0,
  });

  const channelPickupAddressesMapped = useMemo(() => {
    if (!channelAddressData?.results) return [];
    return channelAddressData.results.map((item: any) => ({
      pickup_id: item.id + 1000000,
      user_id: user?.id,
      contact_name: item.locationName || 'Shopify Location',
      company_name: item.locationName || 'Shopify Location',
      address1: item.address1,
      address2: item.address2,
      city: item.city,
      state: item.province || item.state || '',
      country: item.country || 'India',
      pincode: item.zip,
      email: item.email || '',
      contact_no: item.phone,
      default_status: item.isDefault === '1' ? 1 : 0,
      status: 1,
    }));
  }, [channelAddressData, user?.id]);

  const combinedPickupAddresses = useMemo(() => {
    const list = activePickupAddresses || [];
    const sortedList = [...list].sort((a: any, b: any) => {
      if (a.default_status === 1) return -1;
      if (b.default_status === 1) return 1;
      return 0;
    });
    return [...sortedList, ...channelPickupAddressesMapped];
  }, [activePickupAddresses, channelPickupAddressesMapped]);

  // Auto-select default pickup warehouse if not yet chosen
  useEffect(() => {
    if (!formData.pickup_id && combinedPickupAddresses.length > 0) {
      const defaultLoc = combinedPickupAddresses.find((w: any) => w.default_status === 1) || combinedPickupAddresses[0];
      if (defaultLoc) {
        setFormData((prev) => ({
          ...prev,
          pickup_id: String(defaultLoc.pickup_id),
        }));
      }
    }
  }, [combinedPickupAddresses, formData.pickup_id]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cityStateEditable, setCityStateEditable] = useState(false);
  const { mutate: lookupPincode } = usePincodeLookup();

  const [isWarehouseOpen, setIsWarehouseOpen] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState('');
  const warehouseRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        warehouseRef.current &&
        !warehouseRef.current.contains(event.target as Node)
      ) {
        setIsWarehouseOpen(false);
      }
    };

    if (isWarehouseOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isWarehouseOpen]);

  const filteredWarehouses = useMemo(() => {
    if (!warehouseSearch) return combinedPickupAddresses;
    const lowerSearch = warehouseSearch.toLowerCase();
    return combinedPickupAddresses.filter(
      (w: any) =>
        w.company_name?.toLowerCase().includes(lowerSearch) ||
        w.address1?.toLowerCase().includes(lowerSearch) ||
        w.city?.toLowerCase().includes(lowerSearch) ||
        String(w.pincode).includes(lowerSearch)
    );
  }, [combinedPickupAddresses, warehouseSearch]);

  const selectedWarehouse = combinedPickupAddresses.find(
    (w: any) => String(w.pickup_id) === String(formData.pickup_id)
  );

  // Real-time validation
  useEffect(() => {
    const newErrors: Record<string, string> = { ...errors };

    // Customer Details
    if (formData.contact_name !== undefined) {
      if (formData.contact_name.trim() === '') {
        if (errors.contact_name) newErrors.contact_name = 'Customer name is required';
      } else {
        delete newErrors.contact_name;
      }
    }

    if (formData.contact_no) {
      if (!/^[6-9]\d{9}$/.test(formData.contact_no)) {
        newErrors.contact_no = 'Phone number must be 10 digits and start with 6-9';
      } else {
        delete newErrors.contact_no;
      }
    } else if (errors.contact_no) {
      newErrors.contact_no = 'Phone number is required';
    }

    // Address Details
    if (formData.address1 !== undefined) {
      if (formData.address1.trim() === '') {
        if (errors.address1) newErrors.address1 = 'Address is required';
      } else if (formData.address1.length > 60) {
        newErrors.address1 = 'Address 1 cannot exceed 60 characters';
      } else {
        delete newErrors.address1;
      }
    }

    if (formData.address2 !== undefined) {
      if (formData.address2.length > 60) {
        newErrors.address2 = 'Address 2 cannot exceed 60 characters';
      } else {
        delete newErrors.address2;
      }
    }

    if (formData.landmark !== undefined) {
      if (formData.landmark.length > 60) {
        newErrors.landmark = 'Landmark cannot exceed 60 characters';
      } else {
        delete newErrors.landmark;
      }
    }

    if (formData.pincode) {
      if (!/^\d{6}$/.test(formData.pincode)) {
        newErrors.pincode = 'Valid 6-digit pincode is required';
      } else {
        delete newErrors.pincode;
      }
    } else if (errors.pincode) {
      newErrors.pincode = 'Pincode is required';
    }

    if (formData.city !== undefined) {
      if (formData.city.trim() === '') {
        if (errors.city) newErrors.city = 'City is required';
      } else {
        delete newErrors.city;
      }
    }

    if (formData.state !== undefined) {
      if (formData.state.trim() === '') {
        if (errors.state) newErrors.state = 'State is required';
      } else {
        delete newErrors.state;
      }
    }

    // Package Details
    if (formData.weight !== undefined) {
      const weightNum = Number(formData.weight);
      if (!formData.weight || weightNum <= 0 || weightNum > 100) {
        newErrors.weight = 'Weight must be greater than 0 and less than or equal to 100 kg';
      } else {
        delete newErrors.weight;
      }
    }

    // Dimensions validation commented out
    /*
    if (formData.length !== undefined) {
      const lengthNum = Number(formData.length);
      if (!formData.length || lengthNum <= 0) {
        newErrors.length = 'Length must be greater than 0';
      } else {
        delete newErrors.length;
      }
    }

    if (formData.breadth !== undefined) {
      const breadthNum = Number(formData.breadth);
      if (!formData.breadth || breadthNum <= 0) {
        newErrors.breadth = 'Breadth must be greater than 0';
      } else {
        delete newErrors.breadth;
      }
    }

    if (formData.height !== undefined) {
      const heightNum = Number(formData.height);
      if (!formData.height || heightNum <= 0) {
        newErrors.height = 'Height must be greater than 0';
      } else {
        delete newErrors.height;
      }
    }
    */

    if (!formData.pickup_id) {
      if (errors.pickup_id) newErrors.pickup_id = 'Pickup address is required';
    } else {
      delete newErrors.pickup_id;
    }

    if (JSON.stringify(newErrors) !== JSON.stringify(errors)) {
      setErrors(newErrors);
    }
  }, [formData]);

  // Autofill City/State from Pincode lookup API
  useEffect(() => {
    if (formData.pincode.length !== 6) return;

    const timer = setTimeout(() => {
      lookupPincode(formData.pincode, {
        onSuccess: (data) => {
          if (data) {
            setCityStateEditable(false);
            setFormData((prev) => ({
              ...prev,
              city: data.District || prev.city,
              state: data.State || prev.state,
            }));
          } else {
            setCityStateEditable(true);
          }
        },
        onError: () => {
          setCityStateEditable(true);
        },
      });
    }, 400);

    return () => clearTimeout(timer);
  }, [formData.pincode, lookupPincode]);

  // Initial validation check on mount to block submission until edited or if empty
  useEffect(() => {
    const newErrors: Record<string, string> = {};
    if (!formData.address1) newErrors.address1 = 'Address is required';
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.pincode) newErrors.pincode = 'Pincode is required';
    if (!formData.contact_name) newErrors.contact_name = 'Name is required';
    if (!formData.contact_no) newErrors.contact_no = 'Contact number is required';
    if (!formData.weight || Number(formData.weight) <= 0) newErrors.weight = 'Valid weight is required';
    // Initial validation check for dimensions commented out
    /*
    if (!formData.length || Number(formData.length) <= 0) newErrors.length = 'Length is required';
    if (!formData.breadth || Number(formData.breadth) <= 0) newErrors.breadth = 'Breadth is required';
    if (!formData.height || Number(formData.height) <= 0) newErrors.height = 'Height is required';
    */
    if (!formData.pickup_id) newErrors.pickup_id = 'Pickup address is required';
    setErrors(newErrors);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (e.target.name === 'contact_no') {
      value = cleanPhone(value);
    }
    setFormData((prev) => ({ ...prev, [e.target.name]: value }));
  };

  const handleSubmit = async () => {
    if (Object.keys(errors).length > 0) {
      toast.error('Please fill all required fields correctly');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        order_id: order.order_id || order.order_number,
        channel_id: order.channel_id,
        user_id: order.user_id,
        delivery_address: {
          address1: formData.address1,
          address2: formData.address2,
          landmark: formData.landmark,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          contact_name: formData.contact_name,
          contact_no: formData.contact_no,
        },
        package_detail: {
          package_dimension: {
            weight: Number(formData.weight),
            // Dimensions bypassed and hardcoded to 1x1x1 as per requirements
            length: 1,
            width: 1,
            height: 1,
          }
        },
        pickup_address: {
          pickup_id: Number(formData.pickup_id),
        }
      };

      await ordersService.updateChannelOrder(payload);
      toast.success('Order updated successfully');
      
      const updatedOrder = {
        ...order,
        destination_address: {
          ...order.destination_address,
          ...payload.delivery_address,
        },
        origin_address: {
          ...order.origin_address,
          pickup_id: payload.pickup_address.pickup_id,
        },
        dead_weight: payload.package_detail.package_dimension.weight,
        // Hardcoded dimensions returned in updated order state
        length: 1,
        breadth: 1,
        height: 1,
      };
      
      onSuccess(updatedOrder);
    } catch (error) {
      console.error('Update failed:', error);
      toast.error('Failed to update order');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col w-full p-4">
      <h2 className="text-xl font-bold mb-4">Edit Channel Order</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h3 className="font-semibold mb-2">Pickup Address</h3>
          <div className="space-y-3 mb-6">
            <div className="relative w-full" ref={warehouseRef}>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Select Pickup Location *
              </label>

              {/* Trigger */}
              <div
                onClick={() => setIsWarehouseOpen((prev) => !prev)}
                className={`cursor-pointer rounded-md border px-4 py-3 bg-white flex justify-between items-center transition-all ${isWarehouseOpen ? 'border-teal ring-2 ring-teal/10 shadow-sm' : 'hover:border-teal/50'} ${errors.pickup_id ? 'border-red-500' : ''}`}
              >
                <span className="text-sm text-gray-800">
                  {selectedWarehouse
                    ? selectedWarehouse.company_name
                    : 'Select a location'}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform duration-200 ${isWarehouseOpen ? 'rotate-180 text-teal' : ''}`}
                />
              </div>

              {/* Dropdown */}
              {isWarehouseOpen && (
                <div className="absolute z-[100] mt-2 w-full max-h-[300px] flex flex-col rounded-lg border bg-white shadow-xl animate-in fade-in slide-in-from-top-2 duration-200">
                  {/* Search Area */}
                  <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                    <div className="relative">
                      <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={14}
                      />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search by name, address or city..."
                        value={warehouseSearch}
                        onChange={(e) => setWarehouseSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border-none rounded-md focus:ring-1 focus:ring-teal/50 outline-none"
                        onClick={(e) => e.stopPropagation()}
                      />
                      {warehouseSearch && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setWarehouseSearch('');
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full"
                        >
                          <X className="text-gray-400" size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-teal/20">
                    {filteredWarehouses.length === 0 ? (
                      <div className="p-8 text-sm text-gray-400 text-center italic">
                        {warehouseSearch
                          ? 'No matching locations found'
                          : 'No pickup locations found.'}
                      </div>
                    ) : (
                      filteredWarehouses.map((warehouseItem: any) => (
                        <div
                          key={warehouseItem.pickup_id}
                          onClick={() => {
                            setFormData((prev) => ({
                              ...prev,
                              pickup_id: String(warehouseItem.pickup_id),
                            }));
                            setIsWarehouseOpen(false);
                            setWarehouseSearch('');
                          }}
                          className={`hover:bg-teal/5 cursor-pointer border-b border-gray-50 transition-colors ${String(selectedWarehouse?.pickup_id) === String(warehouseItem.pickup_id) ? 'bg-teal/5' : ''}`}
                        >
                          <WarehouseRow warehouse={warehouseItem} rate={true} />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              {errors.pickup_id && <p className="text-red-500 text-xs mt-1">{errors.pickup_id}</p>}
            </div>
          </div>

          <h3 className="font-semibold mb-2">Delivery Details</h3>
          <div className="space-y-3">
            <Input
              label="Contact Name *"
              name="contact_name"
              value={formData.contact_name}
              onChange={handleChange}
              error={errors.contact_name}
            />
            <Input
              label="Contact No *"
              name="contact_no"
              value={formData.contact_no}
              onChange={handleChange}
              error={errors.contact_no}
            />
            <Input
              label="Address 1 *"
              name="address1"
              value={formData.address1}
              onChange={handleChange}
              error={errors.address1}
            />
            <Input
              label="Address 2"
              name="address2"
              value={formData.address2}
              onChange={handleChange}
            />
            <Input
              label="Landmark"
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
            />
            <div className="grid grid-cols-3 gap-2">
              <Input
                label="City *"
                name="city"
                value={formData.city}
                onChange={handleChange}
                error={errors.city}
                disabled={!cityStateEditable}
              />
              <Input
                label="State *"
                name="state"
                value={formData.state}
                onChange={handleChange}
                error={errors.state}
                disabled={!cityStateEditable}
              />
              <Input
                label="Pincode *"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                error={errors.pincode}
              />
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-semibold mb-2">Package Details</h3>
          <div className="space-y-3">
            <Input
              label="Weight (kg) *"
              name="weight"
              type="number"
              value={formData.weight}
              onChange={handleChange}
              error={errors.weight}
            />
            {/* Length, Breadth, Height commented out for now */}
            {/* 
            <div className="grid grid-cols-3 gap-2">
              <Input
                label="Length (cm) *"
                name="length"
                type="number"
                value={formData.length}
                onChange={handleChange}
                error={errors.length}
              />
              <Input
                label="Breadth (cm) *"
                name="breadth"
                type="number"
                value={formData.breadth}
                onChange={handleChange}
                error={errors.breadth}
              />
              <Input
                label="Height (cm) *"
                name="height"
                type="number"
                value={formData.height}
                onChange={handleChange}
                error={errors.height}
              />
            </div>
            */}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting || Object.keys(errors).length > 0}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};
